import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { BackgroundTileCrop } from "@/components/Backgrounds/BackgroundTileCrop";
import {
  FACET_TITLES,
  facetLabel,
  modelTierLabel,
  SOURCE_LABELS,
  TIER_LABELS,
} from "@/components/Backgrounds/facetLabels";
import { aspectClass } from "@/components/Backgrounds/ratios";
import { RefineBackgroundDialog } from "@/components/Backgrounds/RefineBackgroundDialog";
import { HoloShimmer } from "@/components/Holo";
import { formatRelativeTime } from "@/components/reviews/utils";
import { Button, Checkbox, confirmDialog, Dialog, Skeleton, Switch, Textarea } from "@/components/ui";
import { cn } from "@/lib/utils";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import {
  BACKGROUND_FACETS,
  BackgroundAsset,
  BackgroundAssetPatch,
  BackgroundAssetVersion,
  BackgroundFacet,
  BackgroundRatio,
} from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";
import { OptionSelect } from "./LibraryToolbar";
import { PROMOTE_CONFIRM } from "./SelectionBar";

const GENERIC_ERROR = "Something went wrong. Try again.";
const AUTO_ADVANCE_KEY = "backgroundReview.autoAdvance";

const RATIO_ORDER: BackgroundRatio[] = ["square_1_1", "portrait_4_5", "portrait_9_16"];
export const RATIO_LABELS: Record<BackgroundRatio, string> = {
  square_1_1: "1:1",
  portrait_4_5: "4:5",
  portrait_9_16: "9:16",
  landscape_4_3: "4:3",
};

type Flag = "hasPeople" | "hasText" | "hasLogo" | "isBusy" | "videoSafe";
const FLAGS: { flag: Flag; label: string }[] = [
  { flag: "hasPeople", label: "Has people" },
  { flag: "hasText", label: "Has text" },
  { flag: "hasLogo", label: "Has a logo" },
  { flag: "isBusy", label: "Busy scene" },
  { flag: "videoSafe", label: "Video-safe" },
];
const FACETS = Object.keys(BACKGROUND_FACETS) as BackgroundFacet[];
// SimpleSelect needs a value for "untagged"; it goes back to the server as null.
const UNSET = "";

function versionFocal(version: BackgroundAssetVersion): string {
  if (version.focalX == null || version.focalY == null) return "center";
  return `${Math.round(version.focalX * 1000) / 10}% ${Math.round(version.focalY * 1000) / 10}%`;
}

type TagDraft = { description: string } & Record<BackgroundFacet, string> & Record<Flag, boolean>;

function tagDraftFor(asset: BackgroundAsset): TagDraft {
  const draft = { description: asset.description ?? "" } as TagDraft;
  FACETS.forEach(facet => (draft[facet] = asset[facet] ?? UNSET));
  FLAGS.forEach(({ flag }) => (draft[flag] = !!asset[flag]));
  return draft;
}

function tagPatch(asset: BackgroundAsset, draft: TagDraft): BackgroundAssetPatch {
  const patch: BackgroundAssetPatch = {};
  if (draft.description !== (asset.description ?? "")) patch.description = draft.description || null;
  FACETS.forEach(facet => {
    if (draft[facet] !== (asset[facet] ?? UNSET)) patch[facet] = draft[facet] || null;
  });
  FLAGS.forEach(({ flag }) => {
    if (draft[flag] !== !!asset[flag]) patch[flag] = draft[flag];
  });
  return patch;
}

const Section: React.FC<React.PropsWithChildren<{ title?: string; testId: string }>> = ({
  title,
  testId,
  children,
}) => (
  <section className="flex flex-col gap-3" data-testid={testId}>
    {title && <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{title}</div>}
    {children}
  </section>
);

interface VersionTileProps {
  asset: BackgroundAsset;
  ratio: BackgroundRatio;
  version: BackgroundAssetVersion | undefined;
  selected: boolean;
  onSelect(ratio: BackgroundRatio): void;
  onReframe(ratio: BackgroundRatio): void;
  onVersionStatus(version: BackgroundAssetVersion, status: "rejected" | "ready"): void;
  busy: boolean;
}

/** One ratio in the strip under the large preview: pick it to preview, or act on it. */
const VersionTile: React.FC<VersionTileProps> = ({
  asset,
  ratio,
  version,
  selected,
  onSelect,
  onReframe,
  onVersionStatus,
  busy,
}) => {
  const label = RATIO_LABELS[ratio];
  const frame = cn(
    "flex w-full items-center justify-center rounded-lg p-2 text-center text-xs",
    aspectClass(ratio),
    selected && "ring-2 ring-primary ring-offset-1"
  );

  if (!version) {
    return (
      <div className="flex w-28 shrink-0 flex-col gap-1" data-testid={`version-${ratio}`}>
        <button
          type="button"
          onClick={() => onReframe(ratio)}
          disabled={busy}
          className={cn(
            frame,
            "border border-dashed border-slate-300 font-medium text-slate-600 cursor-pointer hover:border-primary hover:text-primary disabled:opacity-60"
          )}
        >
          Create {label}
        </button>
        <span className="text-[11px] text-slate-500">{label}</span>
      </div>
    );
  }

  const isMaster = version.kind === "master";
  return (
    <div className="flex w-28 shrink-0 flex-col gap-1" data-testid={`version-${ratio}`}>
      {version.status === "pending" ? (
        <button
          type="button"
          onClick={() => onSelect(ratio)}
          className="w-full cursor-pointer"
          aria-label={`Preview ${label}`}
        >
          <HoloShimmer
            aspectRatio={ratio}
            showLogo={false}
            className={cn("rounded-lg", selected && "ring-2 ring-primary")}
          />
        </button>
      ) : version.status === "failed" ? (
        <button
          type="button"
          onClick={() => onSelect(ratio)}
          className={cn(frame, "border border-slate-200 bg-slate-50 text-slate-500 cursor-pointer")}
        >
          Couldn't create this size
        </button>
      ) : (
        <BackgroundTileCrop
          asset={{ id: version.id, url: version.url, focal: versionFocal(version), description: asset.description }}
          ratio={ratio}
          selected={selected}
          onClick={() => onSelect(ratio)}
          className={cn(version.status === "rejected" && "opacity-40")}
        />
      )}
      <span className="text-[11px] text-slate-500">
        {[label, version.kind, version.status, modelTierLabel(version.model)].filter(Boolean).join(" · ")}
      </span>
      <div className="flex flex-wrap gap-x-2">
        {version.status === "failed" && (
          <Button variant="link" size="inline" onClick={() => onReframe(ratio)} disabled={busy}>
            Try again
          </Button>
        )}
        {version.status === "ready" && !isMaster && (
          <Button
            variant="destructiveLink"
            size="inline"
            onClick={() => onVersionStatus(version, "rejected")}
            disabled={busy}
          >
            Reject
          </Button>
        )}
        {version.status === "rejected" && (
          <>
            <Button variant="link" size="inline" onClick={() => onVersionStatus(version, "ready")} disabled={busy}>
              Restore
            </Button>
            <Button variant="link" size="inline" onClick={() => onReframe(ratio)} disabled={busy}>
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

/** The selected version as large as the screen allows, at its true shape. */
const VersionPreview: React.FC<{ asset: BackgroundAsset; ratio: BackgroundRatio }> = ({ asset, ratio }) => {
  const version = asset.versions.find(v => v.ratio === ratio);
  const box = cn("h-full max-h-full max-w-full", aspectClass(ratio));
  let content: React.ReactNode;
  if (!version) {
    content = (
      <div
        className={cn(
          box,
          "flex items-center justify-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500"
        )}
      >
        No {RATIO_LABELS[ratio]} version yet
      </div>
    );
  } else if (version.status === "pending") {
    content = <HoloShimmer aspectRatio={ratio} className={cn(box, "w-auto")} />;
  } else if (version.status === "failed") {
    content = (
      <div className={cn(box, "flex items-center justify-center rounded-xl bg-white text-sm text-slate-500")}>
        Couldn't create this size
      </div>
    );
  } else {
    content = (
      <div className={box}>
        <BackgroundTileCrop
          asset={{ id: version.id, url: version.url, focal: versionFocal(version), description: asset.description }}
          ratio={ratio}
          className={cn("h-full", version.status === "rejected" && "opacity-40")}
        />
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-0 items-center justify-center" data-testid="asset-preview">
      {content}
    </div>
  );
};

const CopyPromptButton: React.FC<{ prompt: string }> = ({ prompt }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => {
        void navigator.clipboard?.writeText(prompt);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="text-emerald-600" aria-hidden="true" /> : <Copy aria-hidden="true" />}
      Copy prompt
    </Button>
  );
};

function readAutoAdvance(): boolean {
  try {
    return window.localStorage.getItem(AUTO_ADVANCE_KEY) !== "false";
  } catch {
    return true;
  }
}

function writeAutoAdvance(on: boolean) {
  try {
    window.localStorage.setItem(AUTO_ADVANCE_KEY, String(on));
  } catch {
    // Storage can be blocked; the choice then lasts for this visit only.
  }
}

// Arrow keys page through assets unless the admin is typing or choosing in a control.
function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return true;
  return !!target.closest("[role='combobox'], [role='listbox'], [role='menu']");
}

export interface AssetReviewDialogProps {
  assetId: number | null;
  onClose(): void;
  /** Opens another asset (lineage links, previous and next). */
  onOpenAsset(assetId: number): void;
  /** "New global from this prompt". */
  onNewFromPrompt(prompt: string): void;
  /** The neighbors in the grid's order, for the arrows and auto-advance. */
  previousId?: number | null;
  nextId?: number | null;
  /** 1-based place in the loaded grid. */
  position?: { index: number; total: number } | null;
}

/**
 * One library asset, full screen, for curation: a large preview with its versions side by side,
 * then tags, prompt, lineage, and state in a rail. Arrow keys move through the grid.
 */
export const AssetReviewDialog: React.FC<AssetReviewDialogProps> = ({
  assetId,
  onClose,
  onOpenAsset,
  onNewFromPrompt,
  previousId = null,
  nextId = null,
  position = null,
}) => {
  const queryClient = useQueryClient();
  const open = assetId != null;
  const { data: asset, isLoading } = useQuery({
    queryKey: designKeys.backgrounds.asset(assetId ?? 0),
    queryFn: () => BackgroundAssetServices.get(assetId!),
    enabled: open,
  });
  const { data: children } = useQuery({
    queryKey: [...designKeys.backgrounds.all, "children", assetId],
    queryFn: () => BackgroundAssetServices.list({ parentAssetId: assetId!, perPage: 50 }),
    enabled: open,
  });
  const [draft, setDraft] = React.useState<TagDraft | null>(null);
  const [refineOpen, setRefineOpen] = React.useState(false);
  const [previewRatio, setPreviewRatio] = React.useState<BackgroundRatio | null>(null);
  const [autoAdvance, setAutoAdvance] = React.useState(readAutoAdvance);

  React.useEffect(() => {
    setDraft(asset ? tagDraftFor(asset) : null);
  }, [asset]);

  // Each asset opens on its master.
  React.useEffect(() => setPreviewRatio(null), [assetId]);

  const neighbors = React.useRef({ previousId, nextId, onOpenAsset });
  neighbors.current = { previousId, nextId, onOpenAsset };
  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || isEditingTarget(event.target))
        return;
      const { previousId: prev, nextId: next, onOpenAsset: go } = neighbors.current;
      if (event.key === "ArrowLeft" && prev != null) go(prev);
      if (event.key === "ArrowRight" && next != null) go(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const saved = (next: BackgroundAsset) => {
    queryClient.setQueryData(designKeys.backgrounds.asset(next.id), next);
    queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.all });
  };

  const onError = () => toast.error(GENERIC_ERROR);

  const reframe = useMutation({
    mutationFn: (ratio: BackgroundRatio) => BackgroundAssetServices.reframe(asset!.id, ratio),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.asset(asset!.id) }),
    onError,
  });
  const versionStatus = useMutation({
    mutationFn: ({ version, status }: { version: BackgroundAssetVersion; status: "rejected" | "ready" }) =>
      BackgroundAssetServices.updateVersion(asset!.id, version.id, status),
    onSuccess: saved,
    onError,
  });
  const update = useMutation({
    mutationFn: (patch: BackgroundAssetPatch) => BackgroundAssetServices.update(asset!.id, patch),
    onSuccess: saved,
    onError,
  });
  const promote = useMutation({
    mutationFn: () => BackgroundAssetServices.promote(asset!.id),
    onSuccess: saved,
    onError,
  });

  const saveTags = () => {
    if (!asset || !draft) return;
    update.mutate(tagPatch(asset, draft), { onSuccess: () => toast.success("Tags saved") });
  };

  // Publishing or archiving is the review decision, so it moves on to the next asset when asked to.
  const setVisibility = (visibility: BackgroundAsset["visibility"]) => {
    const next = nextId;
    update.mutate(
      { visibility },
      {
        onSuccess: () => {
          if (autoAdvance && next != null) onOpenAsset(next);
        },
      }
    );
  };

  const onPromote = async () => {
    if (await confirmDialog(PROMOTE_CONFIRM)) promote.mutate();
  };

  // A coach's refine by an admin stays in that coach's library, which the server picks when no scope is sent.
  const refine = async (instruction: string) => {
    if (!asset) return;
    try {
      await BackgroundAssetServices.refine(
        asset.id,
        asset.scope === "global" ? { instruction, scope: "global" } : { instruction }
      );
      queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.requests({}).slice(0, 1) });
    } catch (error) {
      onError();
      throw error;
    }
  };

  const toggleAutoAdvance = (on: boolean) => {
    setAutoAdvance(on);
    writeAutoAdvance(on);
  };

  const versionRatios = asset
    ? [...RATIO_ORDER, ...(asset.versions.some(v => v.ratio === "landscape_4_3") ? (["landscape_4_3"] as const) : [])]
    : [];
  const shownRatio = previewRatio ?? asset?.masterRatio ?? "portrait_4_5";
  const patch = asset && draft ? tagPatch(asset, draft) : {};
  const dirty = Object.keys(patch).length > 0;
  const busy = reframe.isPending || versionStatus.isPending || update.isPending || promote.isPending;
  const childAssets = children?.content ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={next => !next && onClose()}
      size="full"
      className="flex h-screen max-h-screen flex-col overflow-hidden rounded-none"
    >
      <Dialog.Header className="flex flex-row items-center gap-3 border-b border-slate-200 px-5 py-3">
        <Dialog.Title className="min-w-0 flex-1 truncate text-base">
          {asset?.description || "Background photo"}
        </Dialog.Title>
        {position && (
          <span className="text-xs text-slate-500" data-testid="review-position">
            {position.index} of {position.total}
          </span>
        )}
        <Button
          size="icon-sm"
          variant="outline"
          aria-label="Previous background"
          disabled={previousId == null}
          onClick={() => previousId != null && onOpenAsset(previousId)}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <Button
          size="icon-sm"
          variant="outline"
          aria-label="Next background"
          disabled={nextId == null}
          onClick={() => nextId != null && onOpenAsset(nextId)}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
        <Checkbox
          checked={autoAdvance}
          onCheckedChange={on => toggleAutoAdvance(on === true)}
          label="Next after publish or archive"
          aria-label="Next after publish or archive"
        />
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </Dialog.Header>

      <Dialog.Content className="min-h-0 flex-1 overflow-hidden p-0" data-testid="asset-review">
        {isLoading || !asset || !draft ? (
          <div className="grid h-full grid-cols-1 gap-6 p-6 lg:grid-cols-[1fr_400px]">
            <Skeleton className="h-full w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[1fr_400px]">
            <div className="flex min-h-0 flex-col gap-4 bg-slate-100 p-6">
              <div className="min-h-0 flex-1">
                <VersionPreview asset={asset} ratio={shownRatio} />
              </div>
              <Section title="Versions" testId="asset-versions">
                <div className="flex gap-4 overflow-x-auto pb-1">
                  {versionRatios.map(ratio => (
                    <VersionTile
                      key={ratio}
                      asset={asset}
                      ratio={ratio}
                      version={asset.versions.find(v => v.ratio === ratio)}
                      selected={ratio === shownRatio}
                      onSelect={setPreviewRatio}
                      onReframe={r => reframe.mutate(r)}
                      onVersionStatus={(version, status) => versionStatus.mutate({ version, status })}
                      busy={busy}
                    />
                  ))}
                </div>
              </Section>
            </div>

            <div className="flex min-h-0 flex-col gap-6 overflow-y-auto border-l border-slate-200 p-5">
              <Section testId="asset-meta">
                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                  {asset.visibility === "draft" && (
                    <Button size="sm" onClick={() => setVisibility("active")} disabled={busy}>
                      Publish
                    </Button>
                  )}
                  {asset.visibility !== "archived" && (
                    <Button size="sm" variant="outline" onClick={() => setVisibility("archived")} disabled={busy}>
                      Archive
                    </Button>
                  )}
                  {asset.visibility === "archived" && (
                    <Button size="sm" variant="outline" onClick={() => setVisibility("active")} disabled={busy}>
                      Restore
                    </Button>
                  )}
                  {asset.coachId !== null ? (
                    <Button size="sm" variant="outline" onClick={() => void onPromote()} disabled={busy}>
                      Make global
                    </Button>
                  ) : (
                    <span className="font-medium">Global</span>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setRefineOpen(true)}>
                    Refine with AI…
                  </Button>
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs text-slate-600">
                  <dt className="text-slate-400">Usage</dt>
                  <dd>Used in {asset.usageCount} posts</dd>
                  <dt className="text-slate-400">Created</dt>
                  <dd>{[asset.createdBy?.name, formatRelativeTime(asset.createdAt)].filter(Boolean).join(" · ")}</dd>
                  <dt className="text-slate-400">Source</dt>
                  <dd>
                    {[SOURCE_LABELS[asset.source], asset.tier ? TIER_LABELS[asset.tier] : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </dd>
                </dl>
              </Section>

              <Section title="Details" testId="asset-details">
                <Textarea
                  aria-label="Description"
                  value={draft.description}
                  onChange={event => setDraft({ ...draft, description: event.target.value })}
                  rows={3}
                />
                <div className="grid grid-cols-2 gap-3">
                  {FACETS.map(facet => (
                    <div key={facet} className="flex flex-col gap-1">
                      <span className="text-xs text-slate-500">{FACET_TITLES[facet]}</span>
                      <OptionSelect
                        label={FACET_TITLES[facet]}
                        items={[
                          { value: UNSET, label: "—" },
                          ...BACKGROUND_FACETS[facet].map(value => ({ value, label: facetLabel(facet, value) })),
                        ]}
                        value={draft[facet]}
                        onChange={value => setDraft({ ...draft, [facet]: value })}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {FLAGS.map(({ flag, label }) => (
                    <label key={flag} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Switch
                        checked={draft[flag]}
                        onCheckedChange={on => setDraft({ ...draft, [flag]: on })}
                        aria-label={label}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div>
                  <Button size="sm" onClick={saveTags} disabled={!dirty || busy} loading={update.isPending}>
                    Save tags
                  </Button>
                </div>
              </Section>

              {asset.instruction && (
                <Section title="Prompt" testId="asset-prompt">
                  <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
                    {asset.instruction}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <CopyPromptButton prompt={asset.instruction} />
                    <Button size="sm" variant="outline" onClick={() => onNewFromPrompt(asset.instruction!)}>
                      New global from this prompt
                    </Button>
                  </div>
                </Section>
              )}

              {(asset.parentAssetId != null || childAssets.length > 0) && (
                <Section title="Lineage" testId="asset-lineage">
                  {asset.parentAssetId != null && (
                    <div>
                      <Button variant="link" size="inline" onClick={() => onOpenAsset(asset.parentAssetId!)}>
                        Refined from
                      </Button>
                    </div>
                  )}
                  {childAssets.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-slate-700">{childAssets.length} refined versions</span>
                      <div className="grid grid-cols-4 gap-2">
                        {childAssets.map(child => (
                          <BackgroundTileCrop
                            key={child.id}
                            asset={child}
                            ratio={child.masterRatio}
                            onClick={() => onOpenAsset(child.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}
            </div>
          </div>
        )}
      </Dialog.Content>

      {asset && (
        <RefineBackgroundDialog
          open={refineOpen}
          onOpenChange={setRefineOpen}
          asset={{ id: asset.id, url: asset.url, focal: asset.focal }}
          ratio={asset.masterRatio}
          onSubmit={refine}
          busy={false}
        />
      )}
    </Dialog>
  );
};
